import { IDS_URL } from "appConfiguration";
import { useNavigate } from "core/hooks/useNavigate";
import { useRouter } from "next/router";
import { AuthProvider, type AuthProviderProps, useAuth } from "oidc-react";
import { type ReactNode, useEffect } from "react";
import type { AvailableRoutes } from "resources/availableRoutes";
import { ApplicationLoadingDisplay } from "./ApplicationLoadingDisplay";

export const POST_LOGIN_PATH_SESSION_STORAGE_KEY = "post-login-path";
const excludedPaths: AvailableRoutes[] = [
  "/signin-oidc",
  "/logout",
  "/impersonation-callback",
];

function shouldSaveRedirectPath(routerPathname: AvailableRoutes) {
  return !excludedPaths.includes(routerPathname);
}

function AuthLoader({
  children,
  bypass = false,
}: {
  children: ReactNode;
  bypass: boolean;
}) {
  const { userData, userManager } = useAuth();
  useEffect(
    () =>
      userManager.events.addUserLoaded((data) => {
        const { access_token: accessToken, expires_at: expiresAt } = data;
        cookieStore.set({
          name: "authorization_token",
          value: accessToken,
          path: "/",
          sameSite: "strict",
          expires: expiresAt ? expiresAt * 1000 : null,
        });
      }),
    [userManager.events]
  );
  if (bypass) {
    return <>{children}</>;
  }
  if (!userData) {
    return <ApplicationLoadingDisplay />;
  }
  return <>{children}</>;
}

export default function Authorize({ children }: { children: ReactNode }) {
  const { query, asPath, pathname, replace } = useRouter();
  const navigate = useNavigate(false);

  const logoutPathName: AvailableRoutes = "/logout";
  const isLogoutPage = pathname === logoutPathName;

  let autoSignIn = true;

  if (isLogoutPage) {
    autoSignIn = false;
  }

  const configWithRedirects: AuthProviderProps = {
    authority: IDS_URL,
    clientId: "Next_App",
    scope: "openid profile Fixhub offline_access",
    responseType: "code",
    redirectUri: `${window.location.origin}/signin-oidc`,
    postLogoutRedirectUri: `${window.location.origin}/logout`,
    autoSignIn,
    onBeforeSignIn() {
      if (shouldSaveRedirectPath(pathname as AvailableRoutes)) {
        const path =
          query.maintain_locale === "true" ? window.location.pathname : asPath;
        console.log("Saving post-login redirect path:", path);
        sessionStorage.setItem(POST_LOGIN_PATH_SESSION_STORAGE_KEY, path);
        return path;
      }
      return "/";
    },
    async onSignIn() {
      const postLoginPath = sessionStorage.getItem(
        POST_LOGIN_PATH_SESSION_STORAGE_KEY
      );
      console.log("Post-login redirect path:", postLoginPath);
      if (postLoginPath && postLoginPath !== "/") {
        await replace(postLoginPath);
        return;
      }
      await navigate("/");
    },
  };

  return (
    <AuthProvider {...configWithRedirects}>
      <AuthLoader bypass={!autoSignIn}>{children}</AuthLoader>
    </AuthProvider>
  );
}
